import {
  Box,
  Checkbox,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { GoMakeDeleteModal, GoMakeDialog, GomakeTextInput } from "@/components";
import { PrimaryButton } from "@/components/button/primary-button";
import { SecondaryButton } from "@/components/button/secondary-button";
import { PrimaryCheckbox } from "@/components/check-box/primary-check-box";
import { CheckboxCheckedIcon, CheckboxIcon } from "@/icons";
import { FONT_FAMILY } from "@/utils/font-family";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { ModalType } from "@/enums";
import { IReorderSkuGroup, IReorderSkuItem } from "./types";
import { useReorderSkuModal } from "./use-reorder-sku-modal";
import { GoMakeLoadingBackdrop } from "@/widgets/loading-with-shadow/go-make-loading-back-drop";

type ReorderSkuModalProps = Omit<
  ReturnType<typeof useReorderSkuModal>,
  "openReorderSkuModal"
>;

const headerCellSx = (neutralColor: (degree: number) => string) => ({
  ...FONT_FAMILY.Lexend(500, 12),
  color: neutralColor(500),
  borderBottom: "1px solid #E8E8E8",
  paddingInline: "8px",
});

const skuRowGrid = {
  display: "grid",
  gridTemplateColumns: "40px minmax(0, 1fr) 140px",
  alignItems: "center",
  columnGap: "8px",
  paddingInline: "12px",
};

const SkuGroupCard = ({
  group,
  skus,
  selectedRelatedIds,
  isSubmitting,
  primaryColor,
  neutralColor,
  skuDisplayName,
  onToggleGroup,
  onToggleRelated,
  onChangeQuantity,
  t,
}: {
  group: IReorderSkuGroup;
  skus: IReorderSkuItem[];
  selectedRelatedIds: string[];
  isSubmitting: boolean;
  primaryColor: (degree: number) => string;
  neutralColor: (degree: number) => string;
  skuDisplayName: (item: IReorderSkuItem) => string;
  onToggleGroup: (groupId: string) => void;
  onToggleRelated: (skuId: string) => void;
  onChangeQuantity: (skuId: string, quantity: number) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) => {
  const ids = skus.map((item) => item.id);
  const groupState = selectionState(ids, selectedRelatedIds);
  const groupSelected = groupState.checked || groupState.indeterminate;

  return (
    <Box
      sx={{
        border: `1px solid ${groupSelected ? primaryColor(400) : "#E5E7EB"}`,
        borderRadius: "10px",
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap="8px"
        onClick={() => {
          if (!isSubmitting) {
            onToggleGroup(group.id);
          }
        }}
        sx={{
          padding: "8px 12px",
          cursor: isSubmitting ? "default" : "pointer",
          backgroundColor: groupSelected ? primaryColor(100) : "#FFFFFF",
        }}
      >
        <Box onClick={(event) => event.stopPropagation()}>
          <PrimaryCheckbox
            checked={groupState.checked}
            indeterminate={groupState.indeterminate}
            disabled={isSubmitting}
            onChange={() => onToggleGroup(group.id)}
            inputProps={{ "aria-label": group.skuGroupName }}
          />
        </Box>
        <Typography sx={{ ...FONT_FAMILY.Lexend(600, 14), color: "#111827" }}>
          {group.skuGroupName}
        </Typography>
        <Box
          sx={{
            ...FONT_FAMILY.Lexend(500, 12),
            color: primaryColor(700),
            backgroundColor: primaryColor(100),
            borderRadius: "999px",
            padding: "2px 8px",
          }}
        >
          {t("customerSkus.skuCount", { count: skus.length })}
        </Box>
      </Stack>

      <Box sx={{ ...skuRowGrid, paddingTop: "6px", paddingBottom: "4px" }}>
        <Typography sx={{ ...FONT_FAMILY.Lexend(500, 12), color: neutralColor(500) }}>
          {t("customerSkus.select")}
        </Typography>
        <Typography sx={{ ...FONT_FAMILY.Lexend(500, 12), color: neutralColor(500) }}>
          {t("customerSkus.skuName")}
        </Typography>
        <Typography sx={{ ...FONT_FAMILY.Lexend(500, 12), color: neutralColor(500) }}>
          {t("boardMissions.quantity")}
        </Typography>
      </Box>

      <Box
        sx={{
          maxHeight: 176,
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehavior: "contain",
        }}
      >
      {skus.map((item) => {
        const isSelected = selectedRelatedIds.includes(item.id);
        return (
          <Box
            key={item.id}
            onClick={() => {
              if (!isSubmitting) {
                onToggleRelated(item.id);
              }
            }}
            sx={{
              ...skuRowGrid,
              minHeight: 44,
              cursor: isSubmitting ? "default" : "pointer",
              borderTop: "1px solid #F3F4F6",
              "&:hover": {
                backgroundColor: primaryColor(50),
              },
            }}
          >
            <Box onClick={(event) => event.stopPropagation()}>
              <PrimaryCheckbox
                checked={isSelected}
                disabled={isSubmitting}
                onChange={() => onToggleRelated(item.id)}
                inputProps={{ "aria-label": t("customerSkus.select") }}
              />
            </Box>
            <Typography
              sx={{
                ...FONT_FAMILY.Lexend(500, 13),
                color: "#111827",
              }}
            >
              {skuDisplayName(item)}
            </Typography>
            <Box onClick={(event) => event.stopPropagation()}>
              <GomakeTextInput
                type="number"
                value={item.quantity}
                disabled={!isSelected || isSubmitting}
                onChange={(event: { target: { value: string } }) =>
                  onChangeQuantity(item.id, Number(event.target.value))
                }
                inputProps={{ min: 1 }}
                style={{
                  height: 36,
                  boxShadow: "none",
                  WebkitBoxShadow: "none",
                  opacity: isSelected ? 1 : 0.55,
                }}
              />
            </Box>
          </Box>
        );
      })}
      </Box>
    </Box>
  );
};

const selectionState = (ids: string[], selectedIds: string[]) => {
  const selectedCount = ids.filter((id) => selectedIds.includes(id)).length;
  return {
    checked: ids.length > 0 && selectedCount === ids.length,
    indeterminate: selectedCount > 0 && selectedCount < ids.length,
  };
};

const ReorderSkuModal = ({
  isOpen,
  relatedSkus,
  skuGroups,
  selectedRelatedIds,
  isLoadingRelated,
  isSubmitting,
  hasInvalidQuantity,
  isChangeStatusModalOpen,
  closeModal,
  onToggleRelated,
  onToggleGroup,
  onToggleAllGroups,
  onChangeQuantity,
  onReorderWithSameUnitPrice,
  onReorderWithNewCalculation,
  onCloseChangeStatusModal,
  onConfirmChangeStatus,
}: ReorderSkuModalProps) => {
  const { t } = useTranslation();
  const { primaryColor, neutralColor } = useGomakeTheme();
  const dir: "rtl" | "ltr" = t("direction");

  const skuDisplayName = (item: IReorderSkuItem) => {
    if (!item.sku) {
      return t("customerSkus.parentSkuCurrent");
    }
    return item.sku;
  };

  const buttonSx = {
    width: "auto !important",
    minWidth: "max-content",
    flex: "0 0 auto",
    whiteSpace: "nowrap",
    paddingInline: "16px",
    lineHeight: "20px",
  };

  const grouped = skuGroups.length > 0;
  const allSkuIds = relatedSkus.map((item) => item.id);
  const allGroupsState = selectionState(allSkuIds, selectedRelatedIds);
  const fullySelectedGroupCount = skuGroups.filter((group) =>
    selectionState(
      group.skus.map((item) => item.id),
      selectedRelatedIds
    ).checked
  ).length;

  const footer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: grouped ? "space-between" : "flex-end",
        gap: "8px",
        width: "100%",
        direction: dir,
      }}
    >
      {grouped ? (
        <Typography
          sx={{
            ...FONT_FAMILY.Lexend(500, 13),
            color: neutralColor(600),
            flexShrink: 0,
          }}
        >
          {t("customerSkus.skusSelected", { count: selectedRelatedIds.length })}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "8px",
          marginInlineStart: grouped ? "auto" : 0,
        }}
      >
      <SecondaryButton
        onClick={closeModal}
        disabled={isSubmitting || isLoadingRelated}
        variant="outlined"
        sx={buttonSx}
      >
        {t("modal.cancel")}
      </SecondaryButton>
      <PrimaryButton
        onClick={onReorderWithSameUnitPrice}
        disabled={isSubmitting || isLoadingRelated || hasInvalidQuantity}
        variant="contained"
        sx={buttonSx}
      >
        {isSubmitting
          ? t("modal.loading")
          : t("customerSkus.reorderWithSameUnitPrice")}
      </PrimaryButton>
      <PrimaryButton
        onClick={onReorderWithNewCalculation}
        disabled={isSubmitting || isLoadingRelated || hasInvalidQuantity}
        variant="contained"
        sx={buttonSx}
      >
        {isSubmitting
          ? t("modal.loading")
          : t("customerSkus.reorderWithNewCalculation")}
      </PrimaryButton>
      </Box>
    </Box>
  );

  return (
    <>
      <GoMakeDialog
        open={isOpen}
        onClose={closeModal}
        title={t("customerSkus.selectRelatedSkus")}
        maxWidth="md"
        showCloseButton={!isSubmitting}
        customStyles={{
          paper: { minWidth: 640 },
          footer: {
            justifyContent: "stretch",
            overflow: "visible",
            width: "100%",
          },
        }}
        disableEscapeKeyDown={isSubmitting}
        sx={{ zIndex: 1_000_000_000, pointerEvents: isSubmitting ? "none" : "auto" }}
        hidePrimaryButton
        hideCancelButton
        customFooter={footer}
      >
        <Stack
          gap="16px"
          sx={{ paddingTop: "8px", direction: dir, textAlign: "start" }}
        >
          {isLoadingRelated ? (
            <Stack alignItems="center" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <>
              <Stack
                direction="row"
                alignItems="center"
                gap="12px"
                sx={{
                  backgroundColor: primaryColor(100),
                  borderRadius: "10px",
                  padding: "12px 14px",
                }}
              >
                <InfoOutlinedIcon
                  sx={{ color: primaryColor(500), fontSize: 22, flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    ...FONT_FAMILY.Lexend(500, 13),
                    color: primaryColor(500),
                  }}
                >
                  {t("customerSkus.selectRelatedHint")}
                </Typography>
              </Stack>

              {grouped ? (
                <Stack gap="10px" sx={{ direction: dir }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="12px"
                    onClick={() => {
                      if (!isSubmitting) {
                        onToggleAllGroups();
                      }
                    }}
                    sx={{ cursor: isSubmitting ? "default" : "pointer" }}
                  >
                    <Stack direction="row" alignItems="center" gap="8px">
                      <Box onClick={(event) => event.stopPropagation()}>
                        <PrimaryCheckbox
                          checked={allGroupsState.checked}
                          indeterminate={allGroupsState.indeterminate}
                          disabled={isSubmitting}
                          onChange={() => onToggleAllGroups()}
                          inputProps={{
                            "aria-label": t("customerSkus.selectAllGroups"),
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{ ...FONT_FAMILY.Lexend(600, 13), color: "#111827" }}
                      >
                        {t("customerSkus.selectAllGroups")}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        ...FONT_FAMILY.Lexend(500, 12),
                        color: neutralColor(500),
                        flexShrink: 0,
                      }}
                    >
                      {t("customerSkus.groupsSelected", {
                        selected: fullySelectedGroupCount,
                        total: skuGroups.length,
                      })}
                    </Typography>
                  </Stack>

                  <Stack gap="10px">
                    {skuGroups.map((group) => (
                      <SkuGroupCard
                        key={group.id}
                        group={group}
                        skus={group.skus
                          .map((sku) => relatedSkus.find((item) => item.id === sku.id))
                          .filter((item): item is IReorderSkuItem => !!item)}
                        selectedRelatedIds={selectedRelatedIds}
                        isSubmitting={isSubmitting}
                        primaryColor={primaryColor}
                        neutralColor={neutralColor}
                        skuDisplayName={skuDisplayName}
                        onToggleGroup={onToggleGroup}
                        onToggleRelated={onToggleRelated}
                        onChangeQuantity={onChangeQuantity}
                        t={t}
                      />
                    ))}
                  </Stack>
                </Stack>
              ) : (
              <Table size="small" sx={{ direction: dir }}>
                <TableHead>
                  <TableRow>
                    <TableCell width={56} sx={headerCellSx(neutralColor)}>
                      {t("customerSkus.select")}
                    </TableCell>
                    <TableCell sx={headerCellSx(neutralColor)}>
                      {t("customerSkus.skuName")}
                    </TableCell>
                    <TableCell width={140} sx={headerCellSx(neutralColor)}>
                      {t("boardMissions.quantity")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {relatedSkus.map((item) => {
                    const isSelected = selectedRelatedIds.includes(item.id);
                    return (
                      <TableRow
                        key={item.id}
                        onClick={() => {
                          if (!isSubmitting) {
                            onToggleRelated(item.id);
                          }
                        }}
                        sx={{
                          cursor: isSubmitting ? "default" : "pointer",
                          backgroundColor: isSelected
                            ? primaryColor(100)
                            : "transparent",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? primaryColor(200)
                              : primaryColor(50),
                          },
                          "& td": {
                            borderBottom: "1px solid #EEEEEE",
                            paddingInline: "8px",
                          },
                        }}
                      >
                        <TableCell width={56}>
                          <Checkbox
                            checked={isSelected}
                            disabled={isSubmitting}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => onToggleRelated(item.id)}
                            icon={<CheckboxIcon />}
                            checkedIcon={
                              <CheckboxCheckedIcon fill="var(--theme-primary-color, #2E3092)" />
                            }
                            inputProps={{
                              "aria-label": t("customerSkus.select"),
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              ...FONT_FAMILY.Lexend(500, 13),
                              color: isSelected ? primaryColor(700) : "#111827",
                            }}
                          >
                            {skuDisplayName(item)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          width={140}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <GomakeTextInput
                            type="number"
                            value={item.quantity}
                            disabled={!isSelected || isSubmitting}
                            onChange={(event: { target: { value: string } }) =>
                              onChangeQuantity(item.id, Number(event.target.value))
                            }
                            inputProps={{ min: 1 }}
                            style={{
                              height: 36,
                              boxShadow: "none",
                              WebkitBoxShadow: "none",
                              opacity: isSelected ? 1 : 0.55,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              )}
            </>
          )}
        </Stack>
      </GoMakeDialog>
      <GoMakeLoadingBackdrop loading={isSubmitting} />
      <GoMakeDeleteModal
        modalType={ModalType.WARNING}
        title={t("sales.quote.titleModal")}
        subTitle={t("sales.quote.subTitleModal")}
        yesBtn={t("sales.quote.changeStatus")}
        openModal={isChangeStatusModalOpen}
        onClose={onCloseChangeStatusModal}
        onClickDelete={onConfirmChangeStatus}
      />
    </>
  );
};

export { ReorderSkuModal };
